import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {ConfigProvider, Switch} from "antd";
import {DeliveriesShortcuts, deliveryItemHeaderConfig, DeliveryShortcutsKeys} from "./consts.jsx";
import {DeliveryProviderDeliveriesContext} from "./DeliveryProviderDeliveriesContext.jsx";
import {DeliveryProviderDeliveriesExternalDriverModal} from "./DeliveryProviderDeliveriesExternalDriverModal";
import {ItemsListDateRangeMobile} from "../../../components/OrdersDateRangeTable/ItemListViewMobile/ItemsListDateRange.mobile.jsx";
import "./delivery-provider-deliveries.mobile.css";
import {keyBy, uniqBy} from "lodash";
import {StringBuilder} from "../../../AppUtils.js";
import moment from "moment";
import {calculateDeliveryTime} from "../../../event/utils.jsx";

export const DeliveryProviderDeliveriesMobile = ({history}) => {
    const {
        deliveries,
        isLoading,
        drivers,
        setCurrentPage,
        onChangeDeliveryDriver,
        totalDeliveriesCount,
        searchDate,
        showDeliveredDeliveries,
        setShowDeliveredDeliveries,
        openExternalDriverModal,
        closeExternalDriverModal,
        selectedDeliveryId,
        selectedDriverId
    } = useContext(DeliveryProviderDeliveriesContext);
    const [deliveriesLoaded, setDeliveriesLoaded] = useState([]);
    const [loadingDeliveryIds, setLoadingDeliveryIds] = useState([]);
    const [searchInput, setSearchInput] = useState("");

    useEffect(() => {
        if (deliveries?.length) {
            setDeliveriesLoaded(prevDeliveries =>
                uniqBy([...prevDeliveries, ...deliveries], delivery => delivery?.deliveryId)
            );
        }
    }, [deliveries]);

    useEffect(() => {
        setLoadingDeliveryIds([]);
        setDeliveriesLoaded([]);
    }, [searchDate, showDeliveredDeliveries]);

    const driverIdToDriver = useMemo(() => keyBy(drivers, driver => driver?.driverId), [drivers]);

    const onChangeDriver = useCallback(
        async ({deliveryId}, driverId) => {
            const driver = driverIdToDriver[driverId];

            if (driver?.isExternal) {
                openExternalDriverModal(deliveryId, driverId);

                return;
            }

            await handleDriverChange(deliveryId, driverId, false);
        },
        [driverIdToDriver, onChangeDeliveryDriver]
    );

    const handleDriverChange = useCallback(
        async (deliveryId, driverId, syncDeliveries = true, externalDriverData = null) => {
            setLoadingDeliveryIds(prev => [...prev, deliveryId]);
            await onChangeDeliveryDriver(deliveryId, driverId, syncDeliveries, externalDriverData);
            setDeliveriesLoaded(prev => prev.map(d => (d.deliveryId === deliveryId ? {...d, driverId} : d)));
            setLoadingDeliveryIds(prev => prev.filter(d => d !== deliveryId));
        },
        [onChangeDeliveryDriver]
    );

    const filteredDeliveries = useMemo(
        () =>
            deliveriesLoaded?.filter(({companyName, driverId, proposalId, price, order, destinationAddress}) =>
                [
                    companyName,
                    driverId,
                    String(proposalId),
                    String(price),
                    order.providerBrand,
                    destinationAddress?.city,
                    destinationAddress?.address
                ].some(property => property?.toLowerCase()?.includes(searchInput?.toLowerCase()))
            ),
        [searchInput, deliveriesLoaded]
    );

    const handleShowDeliveredDeliveriesToggle = useCallback(checked => {
        setShowDeliveredDeliveries(prev => !prev);
    }, []);

    const handleExternalDriverSave = useCallback(
        async ({name, phone}) => {
            await handleDriverChange(selectedDeliveryId, selectedDriverId, false, {name, phone});
            closeExternalDriverModal();
        },
        [selectedDeliveryId, selectedDriverId, onChangeDeliveryDriver]
    );

    return (
        <div className="delivery-provider-deliveries-mobile-page">
            <ConfigProvider direction="rtl">
                <ItemsListDateRangeMobile
                    singleDay={true}
                    title={(() => {
                        const startDate = moment(searchDate[0]);
                        const endDate = moment(searchDate[1]);

                        const formattedStart = startDate.format("DD/MM/YYYY");
                        const formattedEnd = endDate.format("DD/MM/YYYY");

                        return new StringBuilder(totalDeliveriesCount || "0")
                            .append("משלוחים")
                            .append(" | ")
                            .append(formattedStart)
                            .append(
                                startDate.isSame(endDate, "day")
                                    ? ""
                                    : new StringBuilder(" - ").append(formattedEnd).toString()
                            )
                            .toString();
                    })()}
                    extraContent={
                        <div className="driver-deliveries-show-delivered-deliveries-toggle-container">
                            <Switch
                                onChange={handleShowDeliveredDeliveriesToggle}
                                checked={!showDeliveredDeliveries}
                                title={!showDeliveredDeliveries ? "משלוחים פתוחים" : "משלוחים סגורים"}
                            />
                            <span>{!showDeliveredDeliveries ? "משלוחים פתוחים" : "משלוחים סגורים"}</span>
                        </div>
                    }
                    data={filteredDeliveries ?? []}
                    shortcutSearchOptions={DeliveriesShortcuts}
                    defaultShortcutSearchOptions={DeliveryShortcutsKeys.todayDeliveries}
                    allowDeselectShortcutSearchOptions={false}
                    isLoading={isLoading}
                    showSearchByCreatedAt={false}
                    disableFutureDates={false}
                    totalResultsAmount={totalDeliveriesCount}
                    useLazyLoading={true}
                    onLastItemReach={() => setCurrentPage(prevPageCount => prevPageCount + 1)}
                    panelHeader={{
                        ...deliveryItemHeaderConfig({driverIdToDriver, hideDriverTag: false}),
                        backgroundColor: delivery => {
                            const deliveryTime = calculateDeliveryTime({
                                ...delivery?.order,
                                dtstart: delivery?.dtstart
                            });
                            const hasNoDriver = !delivery?.driverId;
                            const isDeliveryTimePassed = deliveryTime && moment(deliveryTime).isBefore(moment());
                            return isDeliveryTimePassed && hasNoDriver ? "var(--transparent-error-color)" : "white";
                        }
                    }}
                    onInputSearch={value => setSearchInput(value)}
                    panelBody={{
                        actions: [
                            {
                                text: ({driverId}) => (driverId ? driverIdToDriver?.[driverId]?.name : "ציוות שליח"),
                                type: "dropdown",
                                color: ({deliveryId}) => (loadingDeliveryIds?.includes(deliveryId) ? "gray" : "blue"),
                                loading: ({deliveryId}) => loadingDeliveryIds?.includes(deliveryId),
                                options: drivers?.map(({driverId, name, isExternal}) => ({
                                    label: name,
                                    key: driverId,
                                    style: {color: isExternal ? "#1260D1" : "#11344C"}
                                })),
                                onClick: onChangeDriver
                            },
                            {
                                text: "צפייה בפרטי ההזמנה",
                                onClick: ({deliveryId}) => history?.push(`/delivery/${deliveryId}`),
                                color: "grey"
                            }
                        ],
                        style: {
                            borderColor: "var(--secondary-color)"
                        }
                    }}
                />

                <DeliveryProviderDeliveriesExternalDriverModal onSave={handleExternalDriverSave} />
            </ConfigProvider>
        </div>
    );
};
